import { Component, Input, OnInit, Output, EventEmitter, HostListener, ElementRef, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { of, Subject, takeUntil } from 'rxjs';
import { LocalizationService } from '../../services/localization.service';
import { AppliedFilter, BooleanOperatorEnum, EqualOperatorEnum, FilterConfig, NumberOperatorEnum, OperatorEnum, TextOeratorEnum } from 'src/app/core/interfaces/filter.interface';

const IN_FILTER_OPERATOR = { key: 'IN', label: 'One of', labelAr: 'من ضمن' };

@Component({
  selector: 'app-filter-v2',
  templateUrl: './filter-v2.component.html',
  styleUrl: './filter-v2.component.scss'
})
export class FilterV2Component implements OnInit, OnDestroy {
  @Input({ required: true }) filterConfigs: FilterConfig[];

  @Output() appliedFilters = new EventEmitter<AppliedFilter[]>();

  appliedFiltersList: AppliedFilter[] = [];

  isOpen = false;

  labelKey: "label" | "labelAr" = 'label';
  currentLang = "ar";

  currentOption: FilterConfig | null = null;

  private destroy$ = new Subject<void>();

  formGroup?: FormGroup;

  currentOptionDes?: string;

  private multiOptionsRequired = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const v = control.value;
    if (!Array.isArray(v) || v.length === 0) {
      return { required: true };
    }
    return null;
  };

  private resolveOperator(
    operatorKey: string
  ): { key: string; label: string; labelAr: string } | undefined {
    const enums = [
      OperatorEnum,
      BooleanOperatorEnum,
      TextOeratorEnum,
      NumberOperatorEnum,
      EqualOperatorEnum,
    ];
    for (const list of enums) {
      const found = list.find((el) => el.key === operatorKey);
      if (found) {
        return found;
      }
    }
    return undefined;
  }

  onOptionsFilterSelect(event: unknown) {
    if (!this.currentOption) {
      return;
    }
    if (this.currentOption.multiSelect) {
      const items = Array.isArray(event) ? event : [];
      const lang = this.labelKey;
      this.currentOptionDes = items
        .map((i: { [k: string]: string }) => i?.[lang])
        .filter(Boolean)
        .join(', ');
    } else if (
      event &&
      typeof event === 'object' &&
      this.labelKey in (event as object)
    ) {
      this.currentOptionDes = (event as { [k: string]: string })[this.labelKey];
    }
  }

  submit() {
    if (!this.formGroup?.valid) {
      return;
    }
    const formValue = this.formGroup.value as {
      key: string;
      value: unknown;
      operator: string;
    };
    const isMulti =
      this.currentOption?.multiSelect && this.currentOption?.type === 'options';

    let operator: { key: string; label: string; labelAr: string };
    let value: unknown;
    let valueDes: string;

    if (isMulti) {
      const ids = Array.isArray(formValue.value) ? formValue.value : [];
      value = ids;
      operator = IN_FILTER_OPERATOR;
      valueDes = this.currentOptionDes ?? ids.join(', ');
    } else {
      const op = this.resolveOperator(formValue.operator);
      if (!op) {
        return;
      }
      operator = op;
      value = formValue.value;
      valueDes = String(this.currentOptionDes ?? formValue.value ?? '');
    }

    const newFilter: AppliedFilter = {
      key: formValue.key,
      value,
      label: this.currentOption?.label ?? '',
      labelAr: this.currentOption?.labelAr ?? '',
      operator,
      valueDes,
    };

    this.appliedFiltersList.push(newFilter);
    this.appliedFilters.emit(this.appliedFiltersList);
    this.reset();
  }

  private reset() {
    this.isOpen = false;
    this.formGroup?.reset();
    this.currentOption = null;
    this.currentOptionDes = undefined;
  }

  removeFilter(filter: AppliedFilter) {
    this.appliedFiltersList = this.appliedFiltersList.filter(f => f !== filter);
    this.appliedFilters.emit(this.appliedFiltersList);
    this.reset();
  }

  removeAll() {
    this.appliedFiltersList = [];
    this.appliedFilters.emit(this.appliedFiltersList);
    this.reset();
  }

  ngOnInit(): void {
    this.localzationService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.labelKey = lang === 'en' ? 'label' : 'labelAr';
        this.currentLang = lang;
      });

    this.initForm();
  }

  initForm() {
    this.formGroup = this.fb.group({
      key: [null, [Validators.required]],
      value: [null, [Validators.required]],
      value1: [null],
      operator: [null]
    })
  }

  getControl(name: string) {
    return this.formGroup!.get(name) as FormControl;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectColumn(value: FilterConfig) {
    this.formGroup?.get('value')?.setValue(null);
    this.formGroup?.get('value1')?.setValue(null);
    this.formGroup?.get('operator')?.setValue(null);
    this.currentOptionDes = undefined;

    const opCtrl = this.formGroup?.get('operator');
    const valCtrl = this.formGroup?.get('value');

    if (value.multiSelect && value.type === 'options') {
      opCtrl?.clearValidators();
      opCtrl?.setValue('IN');
      valCtrl?.setValidators([this.multiOptionsRequired]);
    } else {
      opCtrl?.setValidators([Validators.required]);
      valCtrl?.setValidators([Validators.required]);
    }
    opCtrl?.updateValueAndValidity({ emitEvent: false });
    valCtrl?.updateValueAndValidity({ emitEvent: false });

    this.currentOption = null;
    setTimeout(() => {
      this.currentOption = value;
    }, 100);
  }

  getFiltersConfig = () => {
    return of(this.filterConfigs);
  }

  getCurrentFilterOptions = (page: number, size: number, search: string) => {
    if (this.currentOption?.options) {
      const options = this.currentOption?.options ?? [];

      return of(options);
    } else if (this.currentOption?.optionsFnc) {
      return this.currentOption?.optionsFnc(page, size, search, []);
    }

    return of([]);
  }

  getCurrentFilterOption = (id: string) => {
    return of(null);
  }

  getOperators = () => {
    // If custom operators are provided, use them
    if (this.currentOption?.allowedOperators && this.currentOption.allowedOperators.length > 0) {
      return of(this.currentOption.allowedOperators);
    }
    // Otherwise, use default operators based on type
    if (this.currentOption?.type === 'options') {
      return of(BooleanOperatorEnum);
    } else if (this.currentOption?.type === 'text') {
      return of(TextOeratorEnum)
    } else if (this.currentOption?.type === 'number') {
      return of(NumberOperatorEnum);
    }
    return of(OperatorEnum);
  }

  @HostListener('document:click', ['$event'])
  clickOut(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const isInsideComponent = this.eRef.nativeElement.contains(target);

    const isInsideChildOverlay = !!target.closest('ng-dropdown-panel');

    if (!isInsideComponent && !isInsideChildOverlay && this.isOpen) {
      this.isOpen = false;
    }
  }

  constructor(private eRef: ElementRef,
    private localzationService: LocalizationService,
    private fb: FormBuilder
  ) { }
}
